import clsx from 'clsx'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { CardMedia, Typography } from '@mui/material'
import { Banner } from '@workspace/frontend-kit'
import CloseIcon from '@mui/icons-material/Close'
import DataSearch from '../../Common/DataSearch'
import CommonDateTimePicker from '../../Common/commonDatePicker/CommonDatePicker'
import useStyles from './_styles'
import { getSearchPatients } from '../../../utils/patient'
import { getSearchRequiredExperts } from '../../../utils/requiredExpert'
import SwitchConfirmDialog from '../../Common/SwitchConfirmDialog/SwitchConfirmDialog'
import { ShiftIcon } from '../../../assets/icons/icons'
import { defaultPalette } from '../../../theme/idsTheme'
import useFocusInterceptor from '../../../hooks/useFocusInterceptor'
import PlannedConsultationPricesButton from './plannedConsultationPricesButton/PlannedConsultationPricesButton'
import FullScreenActionDialog from '../../Common/fullscreenActionDialog/FullScreenActionDialog'
import { FullScreenActionDialogButton } from '../../Common/fullscreenActionDialog/_props'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import { PlannedConsultationEditorFormProps } from './_props'

const PlannedConsultationEditorForm = ({
    consultation,
    errors,
    setErrors,
    onChange,
    isAutoSaveEnabled,
}: PlannedConsultationEditorFormProps) => {
    const currentHcs = useCurrentHcs()
    const classes = useStyles()
    const [pricesDialogOpen, setPricesDialogOpen] = useState(false)
    const { appointmentDate, patientId, patientLibelle, requiredExpertId, requiredExpertLibelle } = consultation

    const pdfSource = `${window._env_.REACT_APP_HDS_HOST}/static/file/legals/TTD_Tarifs_De_Teleconsultation.pdf`
    const {
        isActionRequired: shouldDisplaySecretariatCheck,
        handleDecision: handleSecretariatCheckResponse,
        interceptFocus,
    } = useFocusInterceptor()

    const [textError, setTextError] = React.useState(undefined)

    const closeFullScreenDialogButton: FullScreenActionDialogButton = {
        label: 'Fermer',
        icon: <CloseIcon />,
        colorVariant: 'dark',
        action: () => setPricesDialogOpen(false),
    }

    const searchPatients = getSearchPatients(currentHcs.id)
    const searchRequiredExperts = getSearchRequiredExperts(currentHcs.id)
    /**
     * @description Display values in auto complete combo box
     * @param {Object} item the item object that populate the combo box
     * @return {string} the output libelle
     */
    const renderOption = (item: any) => {
        if (item) {
            return item.libelle
        }
        return ''
    }

    const checkAppointmentDate = (date: any) => {
        const appointmentIsBeforeToday = dayjs().isAfter(date)
        const dateIsNotValid = !dayjs(date).isValid()
        const isAfter2100 = !dayjs(date).isBefore('2100-01-02')

        let success = false

        if (appointmentIsBeforeToday) {
            setErrors((prevState: any) => ({
                ...prevState,
                appointmentDate: true,
            }))
            // @ts-expect-error TS(2345) FIXME: Argument of type '"Le rendez-vous ne peut être ant... Remove this comment to see the full error message
            setTextError("Le rendez-vous ne peut être antérieur à aujourd'hui.")
        } else if (dateIsNotValid) {
            setErrors((prevState: any) => ({
                ...prevState,
                appointmentDate: true,
            }))
            // @ts-expect-error TS(2345) FIXME: Argument of type '"Veuillez saisir une date au for... Remove this comment to see the full error message
            setTextError('Veuillez saisir une date au format jj/mm/aaaa hh:mm')
        } else if (isAfter2100) {
            setErrors((prevState: any) => ({
                ...prevState,
                appointmentDate: true,
            }))
            // @ts-expect-error TS(2345) FIXME: Argument of type '"La téléconsultation ne peut pas... Remove this comment to see the full error message
            setTextError("La téléconsultation ne peut pas être prévue après l'année 2100")
        } else {
            setErrors((prevState: any) => ({
                ...prevState,
                appointmentDate: false,
                overdueDate: false,
            }))
            setTextError(undefined)
            success = true
        }

        return success
    }

    // /!\ ATTENTION /!\ : if the previously selected date is overdue, when the picker opens, the default value will be set to TODAY. May disrupt the intended error handling.
    const handleDateChange = async (dateEvent: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const selectedAppointmentDate = dayjs(dateEvent.target.value, 'YYYY-MM-DDTHH:mm')
            const success = checkAppointmentDate(selectedAppointmentDate)
            if (success) onChange('date', selectedAppointmentDate.format('YYYY-MM-DDTHH:mm'), true)
        } catch (error) {
            console.log(error)
        }
    }

    const handlePatientChange = (data: any) => {
        onChange('patient', data)
    }

    const handleRequiredExpertChange = (data: any) => {
        onChange('requiredExpert', data)
    }

    React.useEffect(() => {
        if (errors.overdueDate) {
            setTextError(
                // @ts-expect-error TS(2345) FIXME: Argument of type '"La date prévue de cette télécon... Remove this comment to see the full error message
                'La date prévue de cette téléconsultation est dépassée, vous devez la décaler ou la supprimer.',
            )
        }
    }, [errors.overdueDate])
    return (
        <div
            className={clsx(classes.PlannedConsultationEditorFormContainer, classes.PlannedConsultationEditorContainer)}
        >
            <Typography className={classes.PlannedConsultationEditorContainerTitle}>Informations</Typography>
            {!isAutoSaveEnabled && (
                <Banner
                    title="Avant de programmer la consultation"
                    text="Contactez le secrétariat médical du médecin pour convenir d’un créneau, puis créez la téléconsultation"
                    severity="INFO"
                />
            )}
            <div className={classes.PlannedConsultationEditorForm}>
                <div className={classes.PlannedConsultationEditorFormRow}>
                    <div className={classes.PlannedConsultationEditorFormItem}>
                        <DataSearch
                            className={clsx(
                                classes.PlannedConsultationEditorFormField,
                                isAutoSaveEnabled && classes.PlannedConsultationEditorFormFieldDisabled,
                            )}
                            id="patient-search"
                            dataCy="dataCy_patient-search"
                            label={`Nom du patient ${!isAutoSaveEnabled ? '*' : ''}`}
                            onDataSelected={handlePatientChange}
                            renderOption={renderOption}
                            initialValue={
                                patientId && patientLibelle
                                    ? {
                                          id: patientId,
                                          libelle: patientLibelle,
                                      }
                                    : undefined
                            }
                            searchDataFunction={searchPatients}
                            disabled={isAutoSaveEnabled}
                            variant="outlined"
                        />
                    </div>
                    <div className={classes.PlannedConsultationEditorFormItem}>
                        <DataSearch
                            className={clsx(
                                classes.PlannedConsultationEditorFormField,
                                isAutoSaveEnabled && classes.PlannedConsultationEditorFormFieldDisabled,
                            )}
                            id="required-expert-search"
                            dataCy="dataCy_required-expert-search"
                            label={`Nom du téléconsultant - Spécialité ${!isAutoSaveEnabled ? '*' : ''}`}
                            onDataSelected={handleRequiredExpertChange}
                            renderOption={renderOption}
                            initialValue={
                                requiredExpertId && requiredExpertLibelle
                                    ? {
                                          id: requiredExpertId,
                                          libelle: requiredExpertLibelle,
                                      }
                                    : undefined
                            }
                            searchDataFunction={searchRequiredExperts}
                            disabled={isAutoSaveEnabled}
                            variant="outlined"
                        />
                    </div>
                </div>
                <div className={classes.PlannedConsultationEditorFormRow}>
                    <div className={classes.PlannedConsultationEditorFormItem}>
                        <CommonDateTimePicker
                            className={classes.PlannedConsultationEditorFormField}
                            defaultValue={appointmentDate}
                            onChange={handleDateChange}
                            onFocusCapture={isAutoSaveEnabled ? interceptFocus : undefined}
                            error={Object.values(errors).includes(true)}
                            dataCy="dataCy_appointmentDate"
                        />
                    </div>
                </div>
            </div>
            {textError && <div className={classes.PlannedConsultationEditorFormError}>{textError}</div>}
            <PlannedConsultationPricesButton onClick={() => setPricesDialogOpen(true)} />
            {shouldDisplaySecretariatCheck && (
                <SwitchConfirmDialog
                    title="Déplacer la téléconsultation"
                    bannerTitle="Avant de déplacer la téléconsultation"
                    bannerText="Contactez le secrétariat médical du médecin pour convenir du nouveau créneau, puis effectuez la mise à jour"
                    switchText="Modification validée par le secrétariat"
                    submitText="Déplacer"
                    onApprove={() => handleSecretariatCheckResponse(true)}
                    onCancel={() => handleSecretariatCheckResponse(false)}
                    saveStartIcon={<ShiftIcon />}
                    submitBackgroundColor={defaultPalette.typography.main}
                />
            )}
            <FullScreenActionDialog
                open={pricesDialogOpen}
                title="Tarifs de téléconsultation"
                cancelProps={undefined}
                saveProps={closeFullScreenDialogButton}
                innerContainerVariant="fullscreen"
            >
                <CardMedia
                    className={classes.PlannedConsultationEditorDocumentViewer}
                    component="iframe"
                    image={pdfSource}
                />
            </FullScreenActionDialog>
        </div>
    )
}

export default PlannedConsultationEditorForm
